<template>
  <div ref="pageRoot" class="iphone-auth-page">
    <div class="auth-shell">
      <div v-if="hasValidSession" class="hero-card session-overview">
        <div class="session-card">
          <div class="session-row">
            <span>当前卡密</span>
            <strong>{{ cardKey }}</strong>
          </div>
          <div class="session-row">
            <span>剩余有效期</span>
            <strong>{{ remainingValidityText }}</strong>
          </div>
          <div class="session-row">
            <span>浏览器标识</span>
            <strong>{{ deviceId }}</strong>
          </div>
        </div>
      </div>

      <div class="form-card">
        <span class="section-tag">身份验证</span>
        <h2>输入卡密后进入首页</h2>
        <p class="form-desc">未认证状态下不能直接进入首页和地图页面。</p>
        <div class="browser-alert" :class="isSafari ? 'is-success' : 'is-error'">
          <span class="browser-alert__icon">{{ isSafari ? '✓' : '!' }}</span>
          <div class="browser-alert__content">
            <strong>{{ isSafari ? '环境检测正常' : '请使用 Safari 浏览器打开' }}</strong>
            <p>{{ browserNoticeText }}</p>
          </div>
        </div>

        <div class="operation-notice">
          <span class="operation-notice__dot" />
          <strong>请使用 WiFi + 室内环境操作</strong>
        </div>

        <label class="input-label" for="card-key-input">卡密</label>
        <input
          id="card-key-input"
          v-model.trim="inputCardKey"
          class="card-input"
          type="text"
          maxlength="64"
          placeholder="请输入卡密"
          @keyup.enter="submitCardKey"
        >

        <button class="video-tutorial-link" type="button" @click="openVideoTutorial">
          第一次使用？查看视频教程
        </button>

        <div class="action-group">
          <button
            class="primary-button"
            :class="{ 'is-ready': canSubmit }"
            type="button"
            :disabled="!canSubmit"
            @click="submitCardKey"
          >
            {{ isSubmitting ? '验证中...' : '验证并进入首页' }}
          </button>
          <div class="quick-action-row">
            <button class="secondary-button compact-button" type="button" @click="openTutorialPage">
              使用教程
            </button>
            <button class="secondary-button compact-button contact-button" type="button" @click="openContactCard">
              获取卡密
            </button>
          </div>
          <button v-if="hasValidSession" class="secondary-button" type="button" :disabled="!isSafari" @click="continueWithCurrentSession">
            继续使用当前卡密
          </button>
          <button v-if="hasValidSession" class="ghost-button" type="button" @click="resetCardSession">
            更换卡密
          </button>
        </div>

        <p class="helper-text">获取卡密请点击上方按钮，扫码加入 QQ 群。</p>
      </div>
    </div>

    <div v-if="showContactCard" class="contact-mask" @click.self="closeContactCard">
      <div class="contact-panel">
        <button class="contact-close" type="button" aria-label="关闭" @click="closeContactCard">×</button>
        <span class="contact-tag">获取卡密</span>
        <h3>扫码加入 QQ 群</h3>
        <p>进群后联系管理员获取卡密，建议使用手机 QQ 扫码。</p>
        <div class="qr-frame">
          <img :src="contactQrImage" alt="QQ 群二维码">
        </div>
        <button class="primary-button is-ready contact-ok-button" type="button" @click="closeContactCard">
          我知道了
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Toast } from 'vant'
import contactQrImage from '@/assets/qq/lxwm.png'
import { isSafariBrowser } from './browser-env'
import {
  clearCardSession,
  getCardSession,
  getOrCreateDeviceId,
  saveCardSession,
} from './card-session'

export default {
  name: 'IphonLocalAuth',
  data() {
    return {
      isSubmitting: false,
      inputCardKey: '',
      cardKey: '',
      deviceId: '',
      cardInfo: {},
      expiresAt: 0,
      lastTouchEndAt: 0,
      showContactCard: false,
      contactQrImage,
      videoTutorialUrl: 'https://www.amgoto.top/video/iphone-tutorial.mp4',
    }
  },
  computed: {
    hasValidSession() {
      return !!this.cardKey && !!this.deviceId && !!this.expiresAt
    },
    isSafari() {
      return isSafariBrowser()
    },
    canSubmit() {
      return this.isSafari && !!this.inputCardKey && !this.isSubmitting
    },
    browserNoticeText() {
      if (this.isSafari) {
        return '当前为 Safari 浏览器，可以继续验证卡密。'
      }
      return '当前浏览器不支持配置文件安装，请复制链接后在 Safari 中重新打开。'
    },
    cardEffectiveEndAt() {
      const effectiveEndTime = this.cardInfo && this.cardInfo.effectiveEndTime
      if (!effectiveEndTime) {
        return 0
      }
      const normalized = String(effectiveEndTime).replace(/-/g, '/')
      const timestamp = new Date(normalized).getTime()
      return Number.isNaN(timestamp) ? 0 : timestamp
    },
    remainingValidityText() {
      if (!this.cardEffectiveEndAt) {
        return '未知'
      }
      const diff = this.cardEffectiveEndAt - Date.now()
      if (diff <= 0) {
        return '已到期'
      }
      const totalMinutes = Math.ceil(diff / 60000)
      if (totalMinutes < 60) {
        return `${totalMinutes} 分钟`
      }
      const totalHours = Math.ceil(totalMinutes / 60)
      if (totalHours < 24) {
        return `${totalHours} 小时`
      }
      const totalDays = Math.ceil(totalHours / 24)
      return `${totalDays} 天`
    },
  },
  mounted() {
    this.restoreSession()
    if (this.$refs.pageRoot) {
      this.$refs.pageRoot.addEventListener('touchend', this.handleTouchEnd, { passive: false })
    }
  },
  beforeDestroy() {
    if (this.$refs.pageRoot) {
      this.$refs.pageRoot.removeEventListener('touchend', this.handleTouchEnd)
    }
  },
  methods: {
    handleTouchEnd(event) {
      if (!event || !event.changedTouches || event.changedTouches.length !== 1) {
        return
      }
      const now = Date.now()
      if (now - this.lastTouchEndAt < 300) {
        event.preventDefault()
      }
      this.lastTouchEndAt = now
    },
    restoreSession() {
      const session = getCardSession()
      if (!session) {
        this.deviceId = getOrCreateDeviceId()
        return
      }
      this.cardKey = session.cardKey
      this.deviceId = session.deviceId
      this.cardInfo = session.cardInfo || {}
      this.expiresAt = session.expiresAt
      this.inputCardKey = session.cardKey
    },
    async validateCard(cardKey, accessType = 'ENTER_PAGE') {
      const res = await axios.get('/api/vpn/card/validate', {
        params: {
          cardKey,
          accessType,
          deviceId: this.deviceId,
        },
      })
      const payload = res.data || {}
      if (payload.code !== 200) {
        throw new Error(payload.msg || '卡密校验失败')
      }
      return payload.data || {}
    },
    async submitCardKey() {
      if (!this.isSafari) {
        Toast.fail('请使用 Safari 浏览器打开')
        return
      }
      if (!this.inputCardKey) {
        Toast.fail('请输入卡密')
        return
      }
      this.isSubmitting = true
      try {
        this.deviceId = getOrCreateDeviceId()
        // alert(this.deviceId)
        const cardInfo = await this.validateCard(this.inputCardKey, 'ENTER_PAGE')
        const session = saveCardSession(this.inputCardKey, this.deviceId, cardInfo)
        this.cardKey = session.cardKey
        this.cardInfo = session.cardInfo || {}
        this.expiresAt = session.expiresAt
        Toast.success('卡密验证成功')
        setTimeout(() => {
          this.$router.replace(this.$route.query.redirect || '/iphon-seting')
        }, 500)
      } catch (error) {
        clearCardSession()
        this.cardKey = ''
        this.cardInfo = {}
        this.expiresAt = 0
        Toast.fail(error.message || '卡密校验失败')
      } finally {
        this.isSubmitting = false
      }
    },
    continueWithCurrentSession() {
      if (!this.isSafari) {
        Toast.fail('请使用 Safari 浏览器打开')
        return
      }
      this.$router.replace(this.$route.query.redirect || '/iphon-seting')
    },
    resetCardSession() {
      clearCardSession()
      this.cardKey = ''
      this.cardInfo = {}
      this.expiresAt = 0
      this.inputCardKey = ''
      this.deviceId = getOrCreateDeviceId()
      Toast('请重新输入卡密')
    },
    openTutorialPage() {
      this.$router.push('/iphon-seting/tutorial')
    },
    openContactCard() {
      this.showContactCard = true
    },
    closeContactCard() {
      this.showContactCard = false
    },
    openVideoTutorial() {
      if (!this.videoTutorialUrl) {
        Toast('视频教程配置中')
        return
      }
      window.open(this.videoTutorialUrl, '_blank')
    },
  },
}
</script>

<style scoped>
.iphone-auth-page {
  min-height: 95vh;
  padding: 18px 14px 24px;
  background:
    radial-gradient(circle at top left, rgba(143, 211, 255, 0.22), transparent 34%),
    radial-gradient(circle at top right, rgba(190, 232, 255, 0.24), transparent 30%),
    linear-gradient(180deg, #f4f9ff 0%, #f8fbff 44%, #f1f6fc 100%);
  color: #0f172a;
  touch-action: manipulation;
  -webkit-text-size-adjust: 100%;
}

.auth-shell {
  display: grid;
  gap: 12px;
}

.hero-card,
.form-card {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.82);
  box-shadow: 0 18px 40px rgba(87, 122, 163, 0.12);
  backdrop-filter: blur(14px);
  border-radius: 24px;
}

.hero-card {
  padding: 18px;
}

.section-tag {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: #eaf4ff;
  color: #3b82f6;
  font-size: 12px;
  font-weight: 600;
}

.form-card h2 {
  margin: 0 0 8px;
  font-size: 28px;
  line-height: 1.15;
  letter-spacing: -0.03em;
}

.form-card h2 {
  font-size: 24px;
  margin-top: 10px;
}

.form-desc {
  margin: 0;
  color: #526377;
  font-size: 14px;
  line-height: 1.7;
}

.session-card {
  padding: 14px 16px;
  border-radius: 18px;
  background: linear-gradient(135deg, #f6fbff, #eef6ff);
}

.session-overview {
  padding: 4px;
}

.session-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  font-size: 13px;
  color: #47607b;
  border-bottom: 1px solid rgba(160, 187, 214, 0.18);
}

.session-row:last-child {
  border-bottom: none;
}

.session-row strong {
  max-width: 62%;
  color: #1e293b;
  text-align: right;
  word-break: break-all;
}

.form-card {
  padding: 18px;
}

.browser-alert {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-top: 14px;
  padding: 14px 16px;
  border: 1px solid rgba(239, 68, 68, 0.16);
  border-radius: 16px;
  background: linear-gradient(135deg, #fff1f2, #fff7ed);
}

.browser-alert__icon {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ef4444;
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
}

.browser-alert__content strong {
  display: block;
  color: #b91c1c;
  font-size: 15px;
  line-height: 1.5;
}

.browser-alert__content p {
  margin: 4px 0 0;
  color: #9a3412;
  font-size: 13px;
  line-height: 1.6;
}

.browser-alert.is-success {
  border-color: rgba(34, 197, 94, 0.18);
  background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
}

.browser-alert.is-success .browser-alert__icon {
  background: #22c55e;
}

.browser-alert.is-success .browser-alert__content strong {
  color: #15803d;
}

.browser-alert.is-success .browser-alert__content p {
  color: #3f6750;
}

.operation-notice {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 10px;
  padding: 11px 14px;
  border-radius: 14px;
  background: #fff1f2;
  color: #be123c;
  font-size: 13px;
  line-height: 1.5;
}

.operation-notice__dot {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
}

.input-label {
  display: block;
  margin: 18px 0 8px;
  font-size: 13px;
  color: #456079;
}

.card-input {
  width: 100%;
  height: 52px;
  padding: 0 16px;
  border: 1px solid rgba(164, 189, 214, 0.34);
  border-radius: 16px;
  background: #f8fbff;
  outline: none;
  color: #0f172a;
  font-size: 16px;
  box-sizing: border-box;
}

.video-tutorial-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 42px;
  margin-top: 12px;
  border: 1px solid rgba(96, 165, 250, 0.22);
  border-radius: 14px;
  background: linear-gradient(135deg, #f7fbff, #eff7ff);
  color: #2563eb;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.action-group {
  margin-top: 16px;
  display: grid;
  gap: 10px;
}

.quick-action-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.primary-button,
.secondary-button,
.ghost-button {
  border: none;
  cursor: pointer;
  transition: opacity 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  touch-action: manipulation;
}

.primary-button,
.secondary-button,
.ghost-button {
  width: 100%;
  min-height: 48px;
  border-radius: 16px;
  font-size: 15px;
  font-weight: 700;
}

.compact-button {
  min-height: 42px;
  border-radius: 14px;
  font-size: 14px;
}

.primary-button {
  background: #dbe8f4;
  color: #8ba1b5;
  box-shadow: none;
}

.primary-button.is-ready {
  background: linear-gradient(135deg, #1677ff, #36a3ff);
  color: #ffffff;
  box-shadow: 0 12px 26px rgba(22, 119, 255, 0.34);
}

.secondary-button {
  background: #f4f8fc;
  color: #35516f;
}

.contact-button {
  background: linear-gradient(135deg, #eef8ff, #e7f6ff);
  color: #1677c8;
}

.ghost-button {
  background: #fff7f7;
  color: #c2410c;
}

.primary-button:disabled {
  opacity: 0.55;
  box-shadow: none;
  cursor: not-allowed;
}

.helper-text {
  margin: 12px 0 0;
  color: #6b7f94;
  font-size: 13px;
  text-align: center;
}

.contact-mask {
  position: fixed;
  inset: 0;
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(8px);
  box-sizing: border-box;
}

.contact-panel {
  position: relative;
  width: min(100%, 360px);
  padding: 24px 20px 20px;
  border-radius: 24px;
  background: #ffffff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
  text-align: center;
}

.contact-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: #f1f5f9;
  color: #64748b;
  font-size: 22px;
  line-height: 32px;
  cursor: pointer;
}

.contact-tag {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: #eaf4ff;
  color: #3b82f6;
  font-size: 12px;
  font-weight: 700;
}

.contact-panel h3 {
  margin: 12px 0 6px;
  color: #0f172a;
  font-size: 22px;
  line-height: 1.25;
}

.contact-panel p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.7;
}

.qr-frame {
  margin: 16px auto;
  padding: 12px;
  border-radius: 20px;
  background: linear-gradient(135deg, #f8fbff, #eef6ff);
  border: 1px solid rgba(147, 197, 253, 0.28);
}

.qr-frame img {
  display: block;
  width: 100%;
  border-radius: 14px;
}

.contact-ok-button {
  min-height: 44px;
  border-radius: 14px;
}

@media (max-width: 480px) {
  .iphone-auth-page {
    padding: 14px 12px 20px;
  }

  .form-card h2 {
    font-size: 24px;
  }

  .quick-action-row {
    gap: 8px;
  }

  .compact-button {
    min-height: 40px;
    font-size: 13px;
  }

  .contact-panel {
    padding: 22px 16px 18px;
  }
}
</style>
